import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Checkbox, Select } from "antd";
import InfinityScrollWrapper from "Components/InfinityScroll/InfinityScrollWrapper";
import { DEFAULT_CAMPAIGNS_PAGESIZE } from "PhishingManager/PhishingManager.constants";

interface PropertyOption {
    value: string;
    label: string;
    count?: number;
}

interface Props {
    propertyOptions: PropertyOption[];
    selectedProperties: string[];
    setSelectedProperties: (properties: string[]) => void;
    allSelected: boolean;
    setAllSelected: (val: boolean) => void;
    onPropertyDeselect: (propertyValue: string) => void;
}

const PropertySelect: React.FC<Props> = ({
    propertyOptions,
    selectedProperties,
    setSelectedProperties,
    allSelected,
    setAllSelected,
    onPropertyDeselect,
}) => {
    const [items, setItems] = useState<PropertyOption[]>(propertyOptions);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchValue, setSearchValue] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const filteredItems = useMemo(() => {
        return items.filter(item =>
            item.label.toLowerCase().includes(searchValue.toLowerCase())
        );
    }, [items, searchValue]);

    const fetchItems = async (pageNumber: number) => {
        if (loading || !hasMore) return;
        setLoading(true);
        await new Promise((res) => setTimeout(res, 1000));
        const newItems = Array.from({ length: DEFAULT_CAMPAIGNS_PAGESIZE }, (_, i) => {
            const id = (pageNumber - 1) * DEFAULT_CAMPAIGNS_PAGESIZE + i + 1;
            return { value: `${id}`, label: `Property ${id}` };
        });
        if (pageNumber === 5) setHasMore(false);
        setItems((prev) => [...prev, ...newItems]);
        setLoading(false);
    };

    const handleSelectAll = useCallback(() => {
        if (allSelected) {
            setAllSelected(false);
            setSelectedProperties([]);
        } else {
            setAllSelected(true);
            setSelectedProperties(filteredItems.map((i) => i.value));
            setSearchValue("");
        }
    }, [allSelected, filteredItems]);

    const dropdownRender = useCallback(() => (
        <>
            <div className="group-recipients-all-option">
                <div className="group-recipients-option">
                    <Checkbox checked={allSelected} onClick={handleSelectAll}>
                        Select All
                    </Checkbox>
                </div>
            </div>

            {dropdownOpen && (
                <>
                    {filteredItems.length === 0 && !loading ? (
                        <div className="group-recipients-option">No properties found</div>
                    ) : (
                        <InfinityScrollWrapper
                            hasMore={hasMore}
                            dataLength={filteredItems.length}
                            onLoadMore={() => {
                                if (!loading && hasMore) {
                                    const nextPage = page + 1;
                                    setPage(nextPage);
                                    fetchItems(nextPage);
                                }
                            }}
                            loading={!searchValue && loading}
                            height={200}
                            scrollableId="property-select-dropdown"
                            loaderText="Loading properties..."
                        >
                            {filteredItems.map((el, index) => (
                                <div className="group-recipients-option" key={el.value + index}>
                                    <Checkbox
                                        onClick={() => {
                                            if (selectedProperties.includes(el.value)) {
                                                setSelectedProperties(
                                                    selectedProperties.filter(v => v !== el.value)
                                                );
                                                onPropertyDeselect(el.value);
                                            } else {
                                                setSelectedProperties([...selectedProperties, el.value]);
                                            }
                                        }}
                                        disabled={allSelected}
                                        checked={allSelected || selectedProperties.includes(el.value)}
                                    >
                                        {el.label}
                                    </Checkbox>
                                </div>
                            ))}
                        </InfinityScrollWrapper>
                    )}
                </>
            )}
        </>
    ), [dropdownOpen, filteredItems, allSelected, selectedProperties, hasMore, loading, page, searchValue, handleSelectAll]);

    useEffect(() => {
        fetchItems(1);
    }, []);

    return (
        <Select
            className="phishing-step3__properties_select"
            mode="multiple"
            style={{ width: "100%" }}
            options={items}
            placeholder="Select option"
            size="large"
            maxTagCount={0}
            showSearch={!allSelected}
            tagRender={() => <span />}
            maxTagPlaceholder={() => (
                <span>
                    {allSelected ? "All Properties" : `${selectedProperties.length} selected`}
                </span>
            )}
            value={selectedProperties}
            onSearch={(val) => setSearchValue(val)}
            onDropdownVisibleChange={(open) => {
                setDropdownOpen(open);
                if (!open) setSearchValue("");
            }}
            getPopupContainer={(trigger) => trigger.parentElement}
            dropdownRender={dropdownRender}
        />
    );
};

export default PropertySelect;